import BaseModel from '@core/model';

import RatePlanApplicationLevelEnum from '@constants/enums/ratePlan/applicationLevel';
import RatePlanCalculationTypeEnum from '@constants/enums/ratePlan/calculationType';
import RatePlanStatusEnum from '@constants/enums/ratePlan/status';
import RatePlanSurchargeApplicationEnum from '@constants/enums/ratePlan/surchargeApplication';
import RatePlanTierApplicationEnum from '@constants/enums/ratePlan/tierApplication';
import RatePlanTypeEnum from '@constants/enums/ratePlan/type';

import RatePlanApi from '@packages/rate-plans/api';

import i18n from '@plugins/i18n';

import Adviser from '@/models/Adviser';

export class RatePlan extends BaseModel {
    static name = 'RatePlan';
    static entity = 'ratePlans';
    static Api = RatePlanApi;

    static fields() {
        return {
            ...super.fields(),
            id: this.attr(null),
            name: this.string(null).nullable(),
            description: this.string(null).nullable(),
            status: this.enum(RatePlanStatusEnum).nullable(),
            type: this.enum(RatePlanTypeEnum).nullable(),
            default: this.boolean(false),
            adviser_id: this.string(null).nullable(),
            adviser: this.belongsTo(Adviser, 'adviser_id'),
            minimum_fee: this.attr(this.defaultMoney).nullable(),
            maximum_fee: this.attr(this.defaultMoney).nullable(),
            min_max_application: this.enum(RatePlanApplicationLevelEnum).nullable(),
            calculation_type: this.enum(RatePlanCalculationTypeEnum).nullable(),
            minimum_aua_threshold: this.attr(this.defaultMoney).nullable(),
            surcharge: this.attr(this.defaultMoney).nullable(),
            surcharge_label: this.string(null).nullable(),
            surcharge_application: this.enum(RatePlanSurchargeApplicationEnum).nullable(),
            tier_application: this.enum(RatePlanTierApplicationEnum).nullable(),
            subscription_count: this.number(null).nullable(),
            is_only_active_of_type: this.boolean(null).nullable(),
            entries: this.attr([])
        };
    }

    get status_label() {
        return this.status ? i18n.t(`enums.rate_plan_status.${this.status}`) : null;
    }

    get type_label() {
        return this.type ? i18n.t(`enums.rate_plan_type.${this.type}`) : null;
    }

    get calculation_type_label() {
        return this.calculation_type ? i18n.t(`enums.rate_plan_calculation_type.${this.calculation_type}`) : null;
    }

    get surcharge_application_label() {
        return this.surcharge_application
            ? i18n.t(`enums.rate_plan_surcharge_application.${this.surcharge_application}`)
            : null;
    }

    get tier_application_label() {
        return this.tier_application ? i18n.t(`enums.rate_plan_tier_application.${this.tier_application}`) : null;
    }

    get min_max_application_label() {
        return this.min_max_application
            ? i18n.t(`enums.rate_plan_application_level.${this.min_max_application}`)
            : null;
    }

    get is_active() {
        return this.status === RatePlanStatusEnum.ACTIVE;
    }

    get is_default() {
        return Boolean(this.default);
    }

    get is_draft() {
        return this.status === RatePlanStatusEnum.DRAFT || this.status === null;
    }

    get can_be_activated() {
        return this.is_draft;
    }

    get can_be_deactivated() {
        return this.is_active && !this.is_default && !(Number(this.subscription_count) > 0);
    }

    get can_be_deprecated() {
        return this.is_active && !this.is_default && this.is_only_active_of_type !== true;
    }

    get can_be_deleted() {
        return this.is_draft;
    }

    static mock(overrides = {}) {
        return {
            id: faker => faker.string.uuid(),
            name: faker => faker.commerce.productName(),
            description: faker => faker.lorem.sentence(),
            status: faker => overrides.status || faker.helpers.arrayElement(Object.values(RatePlanStatusEnum)),
            type: faker => overrides.type || faker.helpers.arrayElement(Object.values(RatePlanTypeEnum)),
            default: faker =>
                overrides.default !== undefined ? Boolean(overrides.default) : faker.datatype.boolean(0.2),
            adviser_id: faker => overrides.adviser_id || faker.string.uuid(),
            minimum_fee: faker => ({ amount: faker.number.int({ min: 0, max: 500 }), currency: 'GBP' }),
            maximum_fee: () => null,
            min_max_application: faker => faker.helpers.arrayElement(Object.values(RatePlanApplicationLevelEnum)),
            calculation_type: faker => faker.helpers.arrayElement(Object.values(RatePlanCalculationTypeEnum)),
            minimum_aua_threshold: () => null,
            surcharge: () => null,
            surcharge_label: () => null,
            surcharge_application: () => null,
            tier_application: faker => faker.helpers.arrayElement(Object.values(RatePlanTierApplicationEnum)),
            subscription_count: () => null,
            is_only_active_of_type: () => null,
            entries: () => [
                {
                    id: 'mock-entry-1',
                    from_amount: { amount: 0, currency: 'GBP' },
                    to_amount: null,
                    from_quantity: null,
                    to_quantity: null,
                    rate: 0.0025,
                    rate_per_unit: null,
                    split_rates: false,
                    eis_rate: null,
                    vct_rate: null,
                    iht_rate: null,
                    pmi_rate: null,
                    other_rate: null,
                    eis_rate_per_unit: null,
                    vct_rate_per_unit: null,
                    iht_rate_per_unit: null,
                    pmi_rate_per_unit: null,
                    other_rate_per_unit: null
                }
            ]
        };
    }
}

export default RatePlan;
